import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CRow } from '@coreui/react-pro';

import { callApi } from '../../../../apiCaller';
import { isOrgUser, sortArrayByProperty, toastError, tryParseJSON } from '../../../../utils';
import {
     API_GUEST_SUBSCRIBER_ADD_CART,
     API_GUEST_SUBSCRIBER_DETAIL_CART,
     API_GUEST_SUBSCRIBER_UPDATE_CART,
     CART_TYPES,
     LOCAL_CART,
     LOCAL_LICENSEE,
     LOCAL_ORG_USER,
} from '../../../../constants';

import images from '../../../../assets/images';

import CenterSpinner from '../../../General/Loadings/CenterSpinner';
import Banner from '../../Shared/Banner/Banner';
import DesignationPackage from '../DesignationPackage';
import SwitchPurchase from '../SwitchPurchase';
import SellingGroupCard from './SellingGroup/SellingGroupCard';
import SellingGroupFilter from './SellingGroup/SellingGroupFilter';

import { SellingGroupAPI } from '../../../../services/apis/sellingGroupAPI';

import { fetchSubscriberSellingGroupsRequest } from './actions';
import { fetchGuestContentManagements } from '../../../../containers/guest/action';
import { fetchSubscriberLicenseesRequest } from '../../Subscriber/Licensees/actions';
import { actionSetCart } from '../../Guest/Cart/actions';

const SellingGroups = () => {
     const dispatch = useDispatch();
     const isCallingSellingGroup = useRef(false);

     const user = useSelector((state) => state.subscriber.user);
     const licensees = useSelector((state) => state.subscriber.licensees);
     const contentManagements = useSelector((state) => state.subscriber.contentManagements);
     const cartItems = useSelector((state) => state.subscriber.cart.items);
     const sellingGroups = useSelector((state) => state.subscriber.sellingGroups);
     const licenseeId = useSelector((state) => state.subscriber.licenseeId) || '';
     const licensee = useSelector((state) => state.subscriber.currentLicensee || JSON.parse(localStorage.getItem(LOCAL_LICENSEE)));
     const sellingGroupFilterSelectedSearch = useSelector((state) => state.subscriber.sellingGroupFilterSelected.search);
     const sellingGroupFilterSelectedItems = useSelector((state) => state.subscriber.sellingGroupFilterSelected.items);

     const userLicenseeId = user?.country ? licensees?.find((item) => item.country.includes(user.country))?.id : null;
     const currentLicenseeId = userLicenseeId || licensee?.value;
     const cartLocalStorage = tryParseJSON(localStorage.getItem(LOCAL_CART));
     const showSwitchPurchase = cartItems?.length > 0 || cartLocalStorage?.cartItems?.length > 0;

     const [cartLocal, setCartLocal] = useState(cartLocalStorage);

     const [isLoadings, setIsLoadings] = useState({
          sellingGroup: false,
          designationPackage: true,
     });
     const [designationPackages, setDesignationPackages] = useState([]);

     const pageSellingGroupTrainingCalendarLink = contentManagements.find((content) => content.key === 'pageSellingGroupTrainingCalendarLink');

     const fetchDataContentManagements = () => {
          if (!contentManagements.length) {
               dispatch(fetchGuestContentManagements());
          }
     };

     const fetchLicensees = () => {
          if (!licensees) {
               dispatch(fetchSubscriberLicenseesRequest());
          }
     };

     const fetchDesignationPackages = async () => {
          const designationPackagesResult = await SellingGroupAPI.getAllDesignationPackagesForClient();
          if (designationPackagesResult) {
               setDesignationPackages(designationPackagesResult);
          }
          setIsLoadings((prevIsLoadings) => ({ ...prevIsLoadings, designationPackage: false }));
     };

     useEffect(() => {
          fetchDataContentManagements();
          fetchLicensees();
          fetchDesignationPackages();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     // Fetch data selling groups client
     const fetchSellingGroupsForClient = async () => {
          if (sellingGroups.length || isCallingSellingGroup.current) return;

          isCallingSellingGroup.current = true;

          setIsLoadings((prevIsLoadings) => ({ ...prevIsLoadings, sellingGroup: true }));
          await dispatch(fetchSubscriberSellingGroupsRequest());
          setIsLoadings((prevIsLoadings) => ({ ...prevIsLoadings, sellingGroup: false }));
     };

     useEffect(() => {
          fetchSellingGroupsForClient();
     }, [isOrgUser(), licensees]); // eslint-disable-line react-hooks/exhaustive-deps

     const handleFilter = useCallback(
          (licenseeId = '', sellingGroups = []) => {
               const newSellingGroups = [...sellingGroups];
               let licenseeID = licenseeId || licensee?.value;

               if (!licenseeID && userLicenseeId) licenseeID = userLicenseeId;

               const sellingGroupFilterSearch = sellingGroupFilterSelectedSearch.trim().toLowerCase();
               const sellingGroupFilterSelectedItemsGroup = sellingGroupFilterSelectedItems.reduce((acc, item) => {
                    if (!acc[item.category]) {
                         acc[item.category] = [];
                    }

                    const [, ...valueParts] = item.value.split('_');
                    const finalValue = valueParts.join('_');
                    acc[item.category].push(finalValue);
                    return acc;
               }, {});

               const collectSelectFilter = {
                    search: sellingGroupFilterSearch,
                    items: sellingGroupFilterSelectedItemsGroup,
               };
               const hasFilter =
                    !!collectSelectFilter.search || Object.keys(collectSelectFilter.items).some((key) => collectSelectFilter.items[key].length);

               const finalFilterSellingGroups = newSellingGroups.filter((newSellingGroup) => {
                    let filterUsed = false;
                    let matchedFilter = false;

                    // Filter by searching
                    if (collectSelectFilter.search) {
                         const matchedHeadline = newSellingGroup.headline.toLowerCase().includes(collectSelectFilter.search);
                         const matchedShortDescription = newSellingGroup.shortDescription.toLowerCase().includes(collectSelectFilter.search);
                         const matchedTrainingMethod = newSellingGroup.trainingMethods.some((trainingMethod) => {
                              const trainingMethodLower = trainingMethod.toLowerCase();

                              return (
                                   trainingMethodLower.includes(collectSelectFilter.search) ||
                                   trainingMethodLower.replace('-', ' ').includes(collectSelectFilter.search)
                              );
                         });

                         filterUsed = true;
                         matchedFilter = matchedHeadline || matchedShortDescription || matchedTrainingMethod;
                    }

                    // Filter by selected training level
                    let allowAssignMatchedFilter = !filterUsed || (filterUsed && matchedFilter);
                    if (allowAssignMatchedFilter && collectSelectFilter.items['Training Level']?.length) {
                         filterUsed = true;
                         matchedFilter = newSellingGroup.trainingLevels.length
                              ? collectSelectFilter.items['Training Level'].some((trainingLevel) =>
                                     newSellingGroup.trainingLevels.includes(trainingLevel)
                                )
                              : false;
                    }

                    // Filter by selected training method
                    allowAssignMatchedFilter = !filterUsed || (filterUsed && matchedFilter);
                    if (allowAssignMatchedFilter && collectSelectFilter.items['Training Method']?.length) {
                         filterUsed = true;
                         const sellingGroupTrainingMethods = [];
                         if (newSellingGroup.package) {
                              sellingGroupTrainingMethods.push('package');
                         }
                         if (newSellingGroup.trainingMethods.length) {
                              sellingGroupTrainingMethods.push(...newSellingGroup.trainingMethods);
                         }

                         matchedFilter = sellingGroupTrainingMethods.length
                              ? collectSelectFilter.items['Training Method'].every((trainingMethod) =>
                                     sellingGroupTrainingMethods.includes(trainingMethod)
                                )
                              : false;
                    }

                    // Filter by selected training for
                    allowAssignMatchedFilter = !filterUsed || (filterUsed && matchedFilter);
                    if (allowAssignMatchedFilter && collectSelectFilter.items['Training For']?.length) {
                         filterUsed = true;
                         matchedFilter = newSellingGroup.products.length
                              ? collectSelectFilter.items['Training For'].every((trainingFor) => {
                                     if (collectSelectFilter.items['Training For'].length > 1) {
                                          return newSellingGroup.products.some((product) => product.purchaseType === CART_TYPES.BOTH);
                                     }

                                     switch (trainingFor) {
                                          case CART_TYPES.INDIVIDUAL:
                                               return newSellingGroup.products.some(
                                                    (product) =>
                                                         product.purchaseType === CART_TYPES.INDIVIDUAL || product.purchaseType === CART_TYPES.BOTH
                                               );
                                          case CART_TYPES.ORGANIZATION:
                                               return newSellingGroup.products.some(
                                                    (product) =>
                                                         product.purchaseType === CART_TYPES.ORGANIZATION || product.purchaseType === CART_TYPES.BOTH
                                               );
                                          default:
                                               return false;
                                     }
                                })
                              : false;
                    }

                    // Filter by selected language
                    allowAssignMatchedFilter = !filterUsed || (filterUsed && matchedFilter);
                    if (allowAssignMatchedFilter && collectSelectFilter.items['Language']?.length) {
                         filterUsed = true;
                         matchedFilter = newSellingGroup.languages.length
                              ? collectSelectFilter.items['Language'].every((language) => newSellingGroup.languages.includes(language))
                              : false;
                    }

                    if (hasFilter) return matchedFilter;

                    return true;
               });

               const sellingGroupsMapping = finalFilterSellingGroups.map((sellingGroup) => {
                    const newProductList =
                         sellingGroup.products?.filter((pro) => pro.licenseeId === licenseeID && pro.purchaseType !== CART_TYPES.INVISIBLE) || [];

                    return {
                         ...sellingGroup,
                         products: newProductList,
                    };
               });

               return sellingGroupsMapping;
          },
          [licensee?.value, sellingGroupFilterSelectedItems, sellingGroupFilterSelectedSearch, userLicenseeId]
     );

     const filterSellingGroups = (sellingGroups) => {
          if (!sellingGroups?.length) return [];

          let newSellingGroupsData = sellingGroups.filter((sellingGroup) => {
               const productOrg = sellingGroup.products.filter((po) => po.purchaseType !== CART_TYPES.INVISIBLE);
               return productOrg.length > 0;
          });

          if (licenseeId || currentLicenseeId) {
               newSellingGroupsData = handleFilter(licenseeId, newSellingGroupsData);
          }

          sortArrayByProperty(newSellingGroupsData, 'sortingOder');

          return newSellingGroupsData;
     };

     const handleSelectOrganization = (organizationId, isSetCartLocal = true) => {
          const cartPayload = {
               organizationId,
               type: CART_TYPES.ORGANIZATION,
          };

          let callApiResult = null;

          if (!cartLocal || cartLocal?.type !== CART_TYPES.ORGANIZATION) {
               callApiResult = callApi(API_GUEST_SUBSCRIBER_ADD_CART, 'POST', cartPayload);
          } else {
               cartPayload.id = cartLocal.id;
               cartPayload.userId = null;

               callApiResult = callApi(API_GUEST_SUBSCRIBER_UPDATE_CART, 'PUT', cartPayload);
          }

          if (!callApiResult) {
               return;
          }

          callApiResult.then((response) => {
               if (response.status === 200) {
                    getCartById(response.data?.id, isSetCartLocal);
               } else {
                    toastError(response);
               }
          });
     };

     const handleSwitchIndividual = () => {
          const cartPayload = {
               userId: user.id,
               type: CART_TYPES.INDIVIDUAL,
          };

          callApi(API_GUEST_SUBSCRIBER_ADD_CART, 'POST', cartPayload).then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               getCartById(response.data?.id);
               localStorage.removeItem(LOCAL_ORG_USER);
          });
     };

     const getCartById = (cartId, isSetCartLocal) => {
          callApi(API_GUEST_SUBSCRIBER_DETAIL_CART.replace(':id', cartId), 'GET').then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const { user, organization, sessionId, ...newCart } = response.data;

               if (isSetCartLocal) {
                    localStorage.setItem(LOCAL_CART, JSON.stringify(newCart));
                    dispatch(actionSetCart(newCart));
               }

               setCartLocal(newCart);
          });
     };

     const sellingGroupItems = filterSellingGroups(sellingGroups) || [];

     return (
          <>
               <Banner
                    title="Purchase CAMS Training Products"
                    description="Purchase a variety of courses, assessments, training, and packages using the CAMS Framework®."
                    scrollSection={{ text: 'See all products below', href: '#product-list' }}
                    bgImageUrl={images.BgBanner}
                    trainingPathway={{
                         caption: 'The CAMS Framework® Training Pathway',
                         image: 'https://training-cams-care-assets.s3.us-east-1.amazonaws.com/media/training-pathway-40338894.png',
                    }}
               />

               <DesignationPackage loading={isLoadings.designationPackage} packages={designationPackages} />

               {showSwitchPurchase && (
                    <div className="section-switch-purchase">
                         <div className={`space-${designationPackages.length ? 40 : 70} space-mb-30`}></div>
                         <div className="container">
                              <SwitchPurchase handleSelectOrganization={handleSelectOrganization} handleSwitchIndividual={handleSwitchIndividual} />
                         </div>
                    </div>
               )}

               <section id="product-list" className="section-product-list">
                    <div className="space-40 space-mb-30"></div>
                    <div className="container">
                         <div className="product-list-header">
                              <h2 className="product-list-title">All CAMS Training Products</h2>
                              {pageSellingGroupTrainingCalendarLink && (
                                   <a
                                        href={pageSellingGroupTrainingCalendarLink.value}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="d-none d-lg-flex product-list-training-calendar"
                                   >
                                        <span>View Live Training Calendar For Individuals</span>
                                        <div className="product-list-training-visit">
                                             <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                  <path
                                                       d="M13.8333 13.3333H2.16667V1.66667H8V0H2.16667C1.24167 0 0.5 0.75 0.5 1.66667V13.3333C0.5 14.25 1.24167 15 2.16667 15H13.8333C14.75 15 15.5 14.25 15.5 13.3333V7.5H13.8333V13.3333ZM9.66667 0V1.66667H12.6583L4.46667 9.85833L5.64167 11.0333L13.8333 2.84167V5.83333H15.5V0H9.66667Z"
                                                       fill="currentColor"
                                                  />
                                             </svg>
                                        </div>
                                   </a>
                              )}
                         </div>
                         <div className="d-flex flex-column flex-lg-row align-items-lg-start product-list-card-wrapper">
                              <SellingGroupFilter />
                              <CRow className="flex-fill">
                                   {isLoadings.sellingGroup && <CenterSpinner />}
                                   {!isLoadings.sellingGroup &&
                                        sellingGroupItems.map((sellingGroup) => (
                                             <SellingGroupCard key={sellingGroup.id} sellingGroup={sellingGroup} />
                                        ))}

                                   {!isLoadings.sellingGroup && !sellingGroupItems.length && (
                                        <p className="product-list-not-found">No training products found</p>
                                   )}
                              </CRow>
                         </div>

                         {pageSellingGroupTrainingCalendarLink && (
                              <div className="d-lg-none text-center">
                                   <a
                                        href={pageSellingGroupTrainingCalendarLink.value}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="d-inline-flex d-lg-none product-list-training-calendar"
                                   >
                                        <span>View Live Training Calendar For Individuals</span>
                                        <div className="product-list-training-visit">
                                             <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                  <path
                                                       d="M13.8333 13.3333H2.16667V1.66667H8V0H2.16667C1.24167 0 0.5 0.75 0.5 1.66667V13.3333C0.5 14.25 1.24167 15 2.16667 15H13.8333C14.75 15 15.5 14.25 15.5 13.3333V7.5H13.8333V13.3333ZM9.66667 0V1.66667H12.6583L4.46667 9.85833L5.64167 11.0333L13.8333 2.84167V5.83333H15.5V0H9.66667Z"
                                                       fill="currentColor"
                                                  />
                                             </svg>
                                        </div>
                                   </a>
                              </div>
                         )}
                    </div>
                    <div className="space-70 space-mb-25"></div>
               </section>
          </>
     );
};

export default SellingGroups;
